
import React, { PropsWithChildren, useCallback, useMemo } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './MidPagePromo.css';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';
import { Video, Image, Section, Row, Title, Col } from '../../importCL';
import { ParseHTML } from '../../utils/rewriteLink/rewriteLink';
export interface IMidPagePromoProps extends MappedComponentProperties {
    backgroundOverlay?: boolean,
    backgroundLayout?: 'auto' | '25' | '50' | '75' | '100';
    backgroundColor?: 'white' | 'light-gray' | 'blue' | 'purple',
    backgroundImagePath?: string,
    mobileBackgroundImagePath?: string,
    mediaAlignmentDesktop?: 'left' | 'right',
    mediaAlignmentMobile?: 'top' | 'bottom',
    fontColor?: 'white' | 'black',
    heading?: string,
    alignHeading?: 'left' | 'center' | 'right',
    subHeading?: string,
    subHeadingTag?: 'h3' | 'h4' | 'h5' | 'h6',
    alignSubHeading?: 'left' | 'center' | 'right',
    bodyText?: string,
    alignBody?: 'left' | 'center' | 'right',
    videoLink?: string,
    imageDesktopPath?: string,
    imageMobilePath?: string,
    altText?: string,
    ctaStyle?: 'solid' | 'ghosted-light' | 'ghosted-dark' | 'link' | 'link-light',
    ctaSize?: 'small' | 'medium' | 'large',
    ctaButtonText?: string,
    ctaLink?: string,
    alignCta?: 'left' | 'center' | 'right',
}

export const MidPagePromoEditConfig = {
    emptyLabel: 'Mid Page Promo',
    isEmpty: (props: PropsWithChildren<IMidPagePromoProps>) => {
        return !props ||
            (!props.backgroundImagePath && !props.mobileBackgroundImagePath && !props.heading
                && !props.subHeading && !props.bodyText && !props.videoLink && !(props.imageDesktopPath || props.imageMobilePath)
                && !props.ctaLink && !props.ctaButtonText && !props.backgroundLayout);
    },
};

const BACKGROUND_COLOR = {
    purple: 'MidPagePromo--bg-purple',
    blue: 'MidPagePromo--bg-blue',
    'light-gray': 'MidPagePromo--bg-light-gray',
    white: 'MidPagePromo--bg-white',
} as const;

// Used for Arranging the HorizontalDesktop layout, 
// this governs the size of the media image, allowing the content side to grow to meet the requesed size
const BACKGROUND_LAYOUT = {
    'auto': 'basis-auto',
    '25': 'basis-3/4',
    '50': 'basis-1/2',
    '75': 'basis-1/4',
    '100': 'basis-0',
} as const;

const FONT_COLOR = {
    white: 'MidPagePromo--font-white',
    black: 'MidPagePromo--font-black',
} as const;

const TEXT_ALIGNMENT = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
} as const;

const MidPagePromo = (props: PropsWithChildren<IMidPagePromoProps>): JSX.Element => {
    const Tag: keyof JSX.IntrinsicElements = (props.subHeadingTag || 'h3');


    const Disclaimer = (): JSX.Element => {
        if (props.videoLink) {
            return (
                <div className='ytdisclaimer bg-[var(--white)] px-3'>
                    <p className='text-xs mt-6'>Credit One Bank uses YouTube API services and by watching the video, you are agreeing to the YouTube Terms of Service (<a href='https://youtube.com/terms'>https://youtube.com/terms</a>) and Google Privacy Policy (<a href='https://www.google.com/policies/privacy'>https://www.google.com/policies/privacy</a>).</p>
                </div>
            )
        }
        return null;
    }

    const Media = ({ className }: { className?: string }): JSX.Element => {
        if (props.backgroundLayout === '100') {
            return <></>;
        }

        if (props.videoLink && props.videoLink.trim() !== '') {
            return (
                <Video
                    className={`MidPagePromo__video flex-[1_1_auto] md:mt-2 min-md:w-2/5 min-md:mx-3 !pb-[26rem]` + (className ? ' ' + className : '')}
                    youtubeEmbedURL={props.videoLink}
                    title={props.heading ? props.heading : 'Youtube Video'}
                    videoDisclaimer={<Disclaimer />}
                />
            );
        }
        return (
            <div 
                className={
                    `MidPagePromo__imageBoundary flex items-center ${BACKGROUND_LAYOUT[props.backgroundLayout ?? 'auto']} ${
                    (props.imageDesktopPath && props.imageDesktopPath.trim() !== '') ? 'MidPagePromo__desktopPresent' : ''} ${
                    (props.imageMobilePath && props.imageMobilePath.trim() !== '') ? 'MidPagePromo__mobilePresent' : ''}`
                }>
                {props.imageDesktopPath
                    && props.imageDesktopPath.trim() !== ''
                    && 
                        <Image
                            className={`MidPagePromo__imageDesktop flex-[1_1_auto]` + (className ? ' ' + className : '')}
                            src={props.imageDesktopPath}
                            alt={props.altText || 'MidPagePromo image'}
                            />
                        }
                {props.imageMobilePath
                    && props.imageMobilePath.trim() !== ''
                    && 
                        <Image
                            className={`MidPagePromo__imageMobile flex-[1_1_auto]` + (className ? ' ' + className : '')}
                            src={props.imageMobilePath}
                            alt={props.altText || 'MidPagePromo image'}
                        />
                }
            </div>
        );
    }

    const Content = (): JSX.Element =>
        <Row className='MidPagePromo__contentRow m-0 flex flex-col flex-[1_1_auto] pt-4 pl-2'>
            <Col className='MidPagePromo__heading-content'>
                {props.heading && props.heading.trim() !== '' && 
                    <Title tag='h2' className={`MidPagePromo__heading ${TEXT_ALIGNMENT[props.alignHeading] ?? ''} ${FONT_COLOR[props.fontColor] ?? ''}`}>
                        <ParseHTML>{props.heading}</ParseHTML>
                    </Title>
                }
                {(props.subHeading && props.subHeading.trim() !== '') ?
                    <Tag
                        className={`MidPagePromo__subHeading my-2 ${TEXT_ALIGNMENT[props.alignSubHeading] ?? ''} ${FONT_COLOR[props.fontColor] ?? ''}`}>
                        <ParseHTML>{props.subHeading}</ParseHTML>
                    </Tag>
                :
                    <Row className='MidPagePromo__subHeadingSpacing mb-3' />
                }
                {props.bodyText && props.bodyText.trim() !== '' && 
                    <p className={`MidPagePromo__description ${TEXT_ALIGNMENT[props.alignBody] ?? ''} ${FONT_COLOR[props.fontColor] ?? ''}`}>
                        <ParseHTML>{props.bodyText}</ParseHTML>
                    </p>
                }
            </Col>
            {props?.ctaButtonText &&
                <Col className={`MidPagePromo__row MidPagePromo__cta MidPagePromo__cta--${props.alignCta ?? 'left'} flex mt-6 mb-8`}>
                    <ButtonWrapper
                        href={props.ctaLink}
                        variant={props.ctaStyle}
                        size={props.ctaSize}
                        target='_self'>
                        <ParseHTML>{props.ctaButtonText}</ParseHTML>
                    </ButtonWrapper>
                </Col>
            }
        </Row>

    return (
        <Section
            className={`MidPagePromo__section MidPagePromo__layoutDesktop--${props.mediaAlignmentDesktop ?? 'none'} MidPagePromo__layoutMobile--${props.mediaAlignmentMobile ?? 'none'}`}
            role='region'
            containerClass='md:pl-0 md:pr-0 flex items-center overflow-y-clip flex-col'
        >
            <div className={`MidPagePromo__layout--lt flex-row md:flex-col ${BACKGROUND_COLOR[props.backgroundColor] ?? ''}`}>
                <Media />
                <Content />
            </div>
            <div className={`MidPagePromo__layout--rb flex-row md:flex-col ${BACKGROUND_COLOR[props.backgroundColor] ?? ''}`}>
                <Content />
                <Media />
            </div>
            <Disclaimer />
        </Section>);
};

export default MidPagePromo;